import { GUEST_CONTACT_PARAM_KEYS } from '../functions/guestContactParams';
import type { CheckoutTheme } from '../types/CheckoutTheme';
import type { GuestContact } from '../types/GuestContact';
import { CancelHooks } from './CancelHooks';
import { CheckoutPreferences } from './CheckoutPreferences';
import type { CheckoutUrlRootBuilder } from './CheckoutUrlRootBuilder';
import { CommunitySubscriptionIntentBuilder } from './CommunitySubscriptionIntentBuilder';
import { CourseIntentBuilder } from './CourseIntentBuilder';
import { DigitalIntentBuilder } from './DigitalIntentBuilder';
import { MeetingIntentBuilder } from './MeetingIntentBuilder';
import { PlatformSubscriptionIntentBuilder } from './PlatformSubscriptionIntentBuilder';
import { PostIntentBuilder } from './PostIntentBuilder';
import { ProductIntentBuilder } from './ProductIntentBuilder';
import { SuccessHooks } from './SuccessHooks';
import { TipIntentBuilder } from './TipIntentBuilder';
import { TokenPackIntentBuilder } from './TokenPackIntentBuilder';

/** The order params are emitted in this stable sequence, for readable URLs. */
const PARAM_ORDER: readonly string[] = [
  'type',
  'c',
  'listing_id',
  'course_slug',
  'variant',
  'qty',
  'meeting_type',
  'meeting_time',
  'meeting_duration',
  'meeting_tz',
  'post_id',
  'post_slug',
  'tip_type',
  'tip_amount',
  'tip_goal',
  'tip_ref',
  'plan',
  'interval',
  'use_paid_trial',
  'commitment_period',
  'agree_to_recv_personalized_offers',
  'consent_to_share_phone_number',
  'theme',
  'eco',
  'eco_apple_pay',
  'eco_position',
  'cc_only',
  'mobile_cta',
  'spe_layout',
  'spe_apple_pay',
  'spe_google_pay',
  'spe_link',
  'link_pay',
  'embedded',
  'coupon',
  'guest_email',
  'guest_phone',
  'guest_phone_country',
  'guest_name',
  'source',
  'source_ref',
  'order_id',
  'redirect_to',
  'cancel_url',
];

/**
 * Fluent builder for the `/checkout` URL — the single, shared way other modules
 * (web links, the CLI, bio buttons) start a checkout. It emits exactly the query
 * params {@link parseCheckoutParams} reads, omitting any the parser already
 * defaults (so URLs stay minimal), and validates the required identity per kind
 * before producing a string. Construct it via the `checkoutUrlBuilder()` factory.
 *
 * As the {@link CheckoutUrlRootBuilder}, it owns the param storage; every
 * sub-builder receives this instance and writes through {@link param}.
 */
export class CheckoutUrlBuilder implements CheckoutUrlRootBuilder {
  private readonly params: Record<string, string> = {};

  /** The single param primitive every sub-builder writes through. */
  param(name: string, value: string | number | null | undefined): this {
    if (value == null || value === '') {
      delete this.params[name];
    } else {
      this.params[name] = String(value);
    }
    return this;
  }

  // ─── Intent (sets `type` + the kind's identity) ─────────────────────────────

  product(configure: (intent: ProductIntentBuilder) => void): this {
    return this.intent('product', new ProductIntentBuilder(this), configure);
  }

  digital(configure: (intent: DigitalIntentBuilder) => void): this {
    return this.intent('digital', new DigitalIntentBuilder(this), configure);
  }

  course(configure: (intent: CourseIntentBuilder) => void): this {
    return this.intent('course', new CourseIntentBuilder(this), configure);
  }

  meeting(configure: (intent: MeetingIntentBuilder) => void): this {
    return this.intent('meeting', new MeetingIntentBuilder(this), configure);
  }

  post(configure: (intent: PostIntentBuilder) => void): this {
    return this.intent('post', new PostIntentBuilder(this), configure);
  }

  tip(configure: (intent: TipIntentBuilder) => void): this {
    return this.intent('tip', new TipIntentBuilder(this), configure);
  }

  tokenPack(configure: (intent: TokenPackIntentBuilder) => void): this {
    return this.intent(
      'token_pack',
      new TokenPackIntentBuilder(this),
      configure,
    );
  }

  communitySubscription(
    configure: (intent: CommunitySubscriptionIntentBuilder) => void,
  ): this {
    return this.intent(
      'community_membership_subscription',
      new CommunitySubscriptionIntentBuilder(this),
      configure,
    );
  }

  platformSubscription(
    configure: (intent: PlatformSubscriptionIntentBuilder) => void,
  ): this {
    return this.intent(
      'platform_subscription',
      new PlatformSubscriptionIntentBuilder(this),
      configure,
    );
  }

  // ─── Theme / preferences / hooks ────────────────────────────────────────────

  /** The 4-way visual theme (`light` | `light-contrast` | `dark` | `dark-contrast`). */
  theme(theme: CheckoutTheme): this {
    this.param('theme', theme);
    return this;
  }

  preferences(configure: (preferences: CheckoutPreferences) => void): this {
    configure(new CheckoutPreferences(this));
    return this;
  }

  onSuccess(configure: (hooks: SuccessHooks) => void): this {
    configure(new SuccessHooks(this));
    return this;
  }

  onCancel(configure: (hooks: CancelHooks) => void): this {
    configure(new CancelHooks(this));
    return this;
  }

  // ─── Order metadata ─────────────────────────────────────────────────────────

  /** A pre-applied coupon / discount code (`coupon`). */
  coupon(code: string): this {
    return this.param('coupon', code);
  }

  /**
   * Carry a guest's already-collected contact into the checkout so its guest
   * form opens pre-filled (e.g. a bio lead-form / guest-contact gate ahead of a
   * booking). `param` drops null/empty, so unset fields — and a signed-in buyer
   * — add nothing. Phone must be the national number (no dial code) the checkout
   * expects, paired with its ISO region in `phoneCountry`. See
   * `functions/guestContactParams`.
   */
  guestContact(contact: GuestContact): this {
    this.param(GUEST_CONTACT_PARAM_KEYS.email, contact.email);
    this.param(GUEST_CONTACT_PARAM_KEYS.phone, contact.phone);
    this.param(GUEST_CONTACT_PARAM_KEYS.phoneCountry, contact.phoneCountry);
    this.param(GUEST_CONTACT_PARAM_KEYS.name, contact.name);
    return this;
  }

  /**
   * Order attribution: the `source` context and its optional type-specific ref
   * (`source_ref`). See `parseCheckoutSource` for the recognized source types;
   * an unrecognized context is carried verbatim and falls back to the store source.
   */
  source(source: string, ref?: string): this {
    return this.param('source', source).param('source_ref', ref);
  }

  /** An existing order id to resume — signals retry mode (`order_id`). */
  orderId(orderId: string): this {
    return this.param('order_id', orderId);
  }

  /**
   * Produce the checkout URL. Relative (`/checkout?…`) by default; pass an
   * `origin` (e.g. `https://shop.example`) to get an absolute URL. Throws if the
   * required identity for the chosen kind is missing.
   */
  url(origin?: string): string {
    this.assertReady();
    const search = new URLSearchParams();
    for (const key of PARAM_ORDER) {
      const value = this.params[key];
      if (value != null && value !== '') {
        search.set(key, value);
      }
    }
    const query = search.toString();
    const path = `/checkout${query ? `?${query}` : ''}`;
    return origin ? new URL(path, origin).toString() : path;
  }

  // ─── Internal ───────────────────────────────────────────────────────────────

  private intent<T>(
    type: string,
    builder: T,
    configure: (intent: T) => void,
  ): this {
    this.param('type', type);
    configure(builder);
    return this;
  }

  /**
   * Mirror {@link parseCheckoutParams}'s required-field rules so a malformed URL
   * fails here, at build time, rather than at the route.
   */
  private assertReady(): void {
    const type = this.params.type;
    if (!type) {
      throw new Error(
        'checkoutUrlBuilder: an intent is required (call .product() / .meeting() / …)',
      );
    }
    const isPlatform =
      type === 'token_pack' || type === 'platform_subscription';
    if (!isPlatform && !this.params.c) {
      throw new Error(`checkoutUrlBuilder: urlCode() is required for ${type}`);
    }
    if (type === 'meeting' || type === 'meeting_slot') {
      this.require('meeting_type', 'meetingType()');
    } else if (type === 'post') {
      if (!this.params.post_id && !this.params.post_slug) {
        throw new Error(
          'checkoutUrlBuilder: postId() or postSlug() is required',
        );
      }
    } else if (type === 'community_membership_subscription') {
      this.require('plan', 'plan()');
      this.require('agree_to_recv_personalized_offers', 'agreeToOffers()');
      this.require('consent_to_share_phone_number', 'consentToSharePhone()');
    } else if (type === 'platform_subscription') {
      this.require('plan', 'plan()');
    } else if (type === 'course') {
      this.require('course_slug', 'slug()');
    } else if (type !== 'tip') {
      // product / digital / token_pack — listing-backed.
      this.require('listing_id', 'listing()');
    }
  }

  private require(param: string, method: string): void {
    if (!(param in this.params)) {
      throw new Error(
        `checkoutUrlBuilder: ${method} is required for this checkout`,
      );
    }
  }
}
